import axios from 'axios'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'
import { API_ENDPOINTS } from '@/config/apiEndpoints'

const webUrl = new BaseWebUrl()
const materialCgEndpoints = API_ENDPOINTS.materialCg
const materialCgApiEndpoints = API_ENDPOINTS.materialCgApi
const MATERIAL_TOOL_VERSION = '2.5.19.1227'
/**
 * 素材 CDN 在开发环境下走 Vite `/api` 代理，避免本地预览和下载时直连 CDN 触发跨域。
 * 生产环境仍保持正式资源地址。
 */
const MATERIAL_CDN_BASE = webUrl.baseMaterialCdn
const materialToolClient = axios.create({
  baseURL: webUrl.baseCg,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

const materialCgApiClient = axios.create({
  baseURL: `${webUrl.baseCg}api/`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

function apiRel(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path
}

export interface MaterialAuth {
  uid: number
  sessionId: string
}

export interface MaterialTopCategory {
  id: string
  identify_name: string
  name: string
  order_id: string
  pic_md5: string
}

export interface MaterialSubCategory {
  extra: string
  goods_count: number
  id: string
  identify_name: string
  level: string
  name: string
  order_id: string
  parent_id: string
  pic_md5: string
  shown: string
}

interface MaterialGoodsRaw {
  id: string
  name: string
  intro: string
  price: string
  attr: string
  add_date: string
  modify_date: string
  base_url: string
  extra: string
  sortrank: string
  hasDoll: string
  auth_flag: string
  bottom_colours: string
  cgworld_hide_flag: string
  cid: string
  eighteen_desc: string
  eighteen_flag: string
  file_size: string
  fps: string
  is_show: boolean
}

interface MaterialSearchGoodsRaw {
  add_date: string
  attr: string
  auth_flag: string
  base_url: string
  cgworld_hide_flag: string
  cid: string
  eighteen_desc: string
  eighteen_flag: string
  extra: string
  gid: string
  hasDoll: string
  intro: string
  modify_date: string
  name: string
  price: string
  sortrank: string
  twopid: number
}

export interface MaterialGoods {
  id: string
  name: string
  intro: string
  price: string
  extra: string
  hasDoll: string
  cid: string
  thumbUrl: string
  /** 搜索接口 `twopid`：商品所属真实顶部分类 ID。 */
  sourceTopCategoryId?: string
}

export interface MaterialGoodsQueryResult {
  list: MaterialGoods[]
  total: number
}

export interface MaterialResource {
  down_count: string
  extra: string
  goods_id: string
  id: string
  introduce: string
  music_time: number
  resmd5: string
}

export interface MaterialCollectionItem {
  goods_id: number
  goods_cate_id: number
}

export interface MaterialSearchParams {
  search: string
  search_type: '1' | '2'
  cid: string
  belong_type: string
  page: number
  limit: number
}

function withAuth(auth: MaterialAuth, params: Record<string, unknown> = {}) {
  return {
    ...params,
    uid: auth.uid,
    sessionid: auth.sessionId,
  }
}

export class MaterialAuthExpiredError extends Error {
  constructor(message = '素材库登录态已失效，请重新登录') {
    super(message)
    this.name = 'MaterialAuthExpiredError'
  }
}

function extractApiList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function ensureMaterialApiSuccess(name: string, data: unknown, auth: MaterialAuth) {
  const payload = (data ?? {}) as Record<string, unknown>
  const code = payload.code
  const ret = payload.ret
  const msg = typeof payload.msg === 'string' ? payload.msg : ''

  if (code === 7) {
    throw new MaterialAuthExpiredError()
  }
  if (ret !== undefined && Number(ret) !== 1) {
    throw new Error(msg || `${name} 接口返回异常`)
  }
}

function getThumbUrl(baseUrl: string): string {
  const md5 = baseUrl.split('|')[1] || baseUrl.split('|')[0] || ''
  return md5 ? `${MATERIAL_CDN_BASE}${md5.slice(0, 2)}/${md5}` : ''
}

function normalizeGoods(raw: MaterialGoodsRaw | MaterialSearchGoodsRaw): MaterialGoods {
  const normalizedId = 'gid' in raw ? raw.gid : raw.id
  const sourceTopCategoryId =
    'twopid' in raw && Number(raw.twopid) > 0 ? String(raw.twopid) : undefined
  return {
    id: normalizedId,
    name: raw.name,
    intro: raw.intro,
    price: raw.price,
    extra: raw.extra,
    hasDoll: raw.hasDoll,
    cid: raw.cid,
    thumbUrl: getThumbUrl(raw.base_url),
    sourceTopCategoryId,
  }
}

export function getMaterialPreviewUrl(md5: string): string {
  return `${MATERIAL_CDN_BASE}${md5.slice(0, 2)}/${md5}`
}

export function getMaterialResourceUrl(md5: string): string {
  return `${MATERIAL_CDN_BASE}${md5.slice(0, 2)}/${md5}`
}

export function getProjectResourceUrl(md5: string): string {
  return buildCurrentProjectResourceUrl(md5)
}

export async function getTopCategories(auth: MaterialAuth): Promise<MaterialTopCategory[]> {
  const { data } = await materialToolClient.get(
    apiRel(materialCgEndpoints.topCategories),
    {
      params: withAuth(auth, { lang: 'chinese', tool_version: MATERIAL_TOOL_VERSION }),
    },
  )
  ensureMaterialApiSuccess('get_top_category_list', data, auth)
  const list = extractApiList<MaterialTopCategory>((data as Record<string, unknown>).list)
  return list
}

export async function getChildCategories(
  auth: MaterialAuth,
  cid: string,
  level: '2' | '3',
): Promise<MaterialSubCategory[]> {
  const { data } = await materialToolClient.get(
    apiRel(materialCgEndpoints.childCategories),
    {
      params: withAuth(auth, { cid, level, lang: 'chinese', tool_version: MATERIAL_TOOL_VERSION }),
    },
  )
  ensureMaterialApiSuccess('get_child_category_list', data, auth)
  const list = extractApiList<MaterialSubCategory>((data as Record<string, unknown>).list)
  return list
}

export async function getGoodsByCategory(
  auth: MaterialAuth,
  params: {
    cid: string
    page: number
    limit: number
    select_type: 0 | 1 | 2
  },
): Promise<MaterialGoodsQueryResult> {
  const { data } = await materialToolClient.get(
    apiRel(materialCgEndpoints.goodsByCategory),
    {
      params: withAuth(auth, { ...params, is_admin: 1 }),
    },
  )
  ensureMaterialApiSuccess('get_goodsinfo_by_categoryid', data, auth)
  const payload = data as Record<string, unknown>
  return {
    list: extractApiList<MaterialGoodsRaw>(payload.list).map(normalizeGoods),
    total: Number(payload.total_count || 0),
  }
}

/**
 * 按商品 ID 批量获取素材商品信息（对齐工具 3.0 GoodsBaseInfo.getGoodsInfoByIds）。
 */
export async function getGoodsInfoByIds(
  auth: MaterialAuth,
  goodsIds: number[],
): Promise<MaterialGoods[]> {
  const gids = goodsIds.filter((id) => id > 0).join(',')
  if (!gids) return []
  const { data } = await materialToolClient.get(apiRel(materialCgEndpoints.goodsBaseInfoList), {
    params: withAuth(auth, { gids, lang: 'chinese' }),
  })
  ensureMaterialApiSuccess('get_baseinfo_list', data, auth)
  return extractApiList<MaterialGoodsRaw>((data as Record<string, unknown>).list).map(normalizeGoods)
}

export async function searchGoods(
  auth: MaterialAuth,
  params: MaterialSearchParams,
): Promise<MaterialGoodsQueryResult> {
  const commonParams = withAuth(auth, {
    search: params.search,
    search_type: params.search_type,
    cid: params.cid,
    belong_type: params.belong_type,
    lang: 'chinese',
  })
  const [countRes, listRes] = await Promise.all([
    materialToolClient.get(apiRel(materialCgEndpoints.searchCount), {
      params: commonParams,
    }),
    materialToolClient.get(apiRel(materialCgEndpoints.searchList), {
      params: {
        ...commonParams,
        page: params.page,
        limit: params.limit,
      },
    }),
  ])
  ensureMaterialApiSuccess('get_search_count', countRes.data, auth)
  ensureMaterialApiSuccess('get_search_list', listRes.data, auth)
  const countPayload = countRes.data as Record<string, unknown>
  const listPayload = listRes.data as Record<string, unknown>
  return {
    list: extractApiList<MaterialSearchGoodsRaw>(listPayload.list).map(normalizeGoods),
    total: Number(countPayload.total_count || 0),
  }
}

export async function getGoodsResources(
  auth: MaterialAuth,
  goodsId: string,
): Promise<MaterialResource[]> {
  const { data } = await materialCgApiClient.get(apiRel(materialCgApiEndpoints.goodsResource), {
    params: withAuth(auth, { gid: goodsId, op: 2, lang: 'chinese' }),
  })
  ensureMaterialApiSuccess(materialCgApiEndpoints.goodsResource, data, auth)
  return extractApiList<MaterialResource>((data as Record<string, unknown>).list)
}

export async function getCollection(auth: MaterialAuth): Promise<MaterialCollectionItem[]> {
  const { data } = await materialCgApiClient.get(apiRel(materialCgApiEndpoints.goodsCollect), {
    params: withAuth(auth, { op: 'get' }),
  })
  ensureMaterialApiSuccess(`${materialCgApiEndpoints.goodsCollect}#get`, data, auth)
  return extractApiList<MaterialCollectionItem>((data as Record<string, unknown>).data)
}

export async function toggleCollection(
  auth: MaterialAuth,
  goodsId: string,
  goodsCateId: string,
): Promise<void> {
  const { data } = await materialCgApiClient.get(apiRel(materialCgApiEndpoints.goodsCollect), {
    params: withAuth(auth, { goods_id: goodsId, goods_cate_id: goodsCateId }),
  })
  ensureMaterialApiSuccess(`${materialCgApiEndpoints.goodsCollect}#toggle`, data, auth)
}
